import React from "react";
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import type { PendingAttachment, ProjectMessageAttachment } from "./types";

// Get file URL - prefer signed URL if available, fallback to direct S3
const getFileUrl = (attachment: { key: string; url?: string }) => {
  if (attachment.url) {
    return attachment.url;
  }
  // Fallback (shouldn't be needed anymore)
  return `https://s3.us-central-1.wasabisys.com/simpallo-files/${attachment.key}`;
};

const getFileIcon = (contentType: string) => {
  if (contentType.startsWith("image/")) {
    return null; // Will show image preview instead
  }
  if (contentType === "application/pdf") {
    return <PictureAsPdfIcon sx={{ fontSize: 40, color: "error.main" }} />;
  }
  if (contentType.includes("document") || contentType.includes("text")) {
    return <DescriptionIcon sx={{ fontSize: 40, color: "info.main" }} />;
  }
  return <InsertDriveFileIcon sx={{ fontSize: 40, color: "text.secondary" }} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Preview for pending attachments (before sending)
interface PendingAttachmentPreviewProps {
  attachment: PendingAttachment;
  onRemove: () => void;
}

export const PendingAttachmentPreview: React.FC<
  PendingAttachmentPreviewProps
> = ({ attachment, onRemove }) => {
  const isImage = attachment.file.type.startsWith("image/");

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        p: 1,
        bgcolor: "action.hover",
        borderRadius: 1,
        border: "1px solid",
        borderColor: attachment.error ? "error.main" : "divider",
        minWidth: 80,
        maxWidth: 120,
      }}
    >
      {/* Remove button */}
      <IconButton
        size="small"
        onClick={onRemove}
        disabled={attachment.uploading}
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          bgcolor: "background.paper",
          boxShadow: 1,
          "&:hover": { bgcolor: "error.light", color: "white" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Preview */}
      {attachment.uploading ? (
        <CircularProgress size={40} />
      ) : isImage && attachment.preview ? (
        <Box
          component="img"
          src={attachment.preview}
          alt={attachment.file.name}
          sx={{
            width: 60,
            height: 60,
            objectFit: "cover",
            borderRadius: 1,
          }}
        />
      ) : (
        getFileIcon(attachment.file.type)
      )}

      {/* Filename */}
      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: attachment.error ? "error.main" : "text.secondary",
        }}
      >
        {attachment.error || attachment.file.name}
      </Typography>
    </Box>
  );
};

// Preview for sent attachments (in message)
interface SentAttachmentPreviewProps {
  attachment: ProjectMessageAttachment;
  compact?: boolean;
}

export const SentAttachmentPreview: React.FC<SentAttachmentPreviewProps> = ({
  attachment,
  compact = false,
}) => {
  const isImage = attachment.contentType.startsWith("image/");
  const fileUrl = getFileUrl(attachment);

  // All attachments open in new tab to avoid CORS issues
  return (
    <Link
      href={fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        bgcolor: "action.hover",
        borderRadius: 1,
        textDecoration: "none",
        "&:hover": { bgcolor: "action.selected" },
      }}
    >
      {isImage ? (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: "info.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 20 }}>🖼️</Typography>
        </Box>
      ) : (
        getFileIcon(attachment.contentType)
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {attachment.filename}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatFileSize(attachment.size)} • Click to open
        </Typography>
      </Box>
    </Link>
  );
};

// Container for multiple pending attachments
interface AttachmentPreviewListProps {
  attachments: PendingAttachment[];
  onRemove: (index: number) => void;
}

export const AttachmentPreviewList: React.FC<AttachmentPreviewListProps> = ({
  attachments,
  onRemove,
}) => {
  if (attachments.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        p: 1,
        bgcolor: "background.default",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      {attachments.map((attachment, index) => (
        <PendingAttachmentPreview
          key={index}
          attachment={attachment}
          onRemove={() => onRemove(index)}
        />
      ))}
    </Box>
  );
};

export default AttachmentPreviewList;
